import type { PermissionAction } from '@/api/admin-access';

export type AdminPermissionKey =
  | 'BusinessClaimsApproval'
  | 'ad_network'
  | 'blog'
  | 'businesses'
  | 'cigars'
  | 'drink_pairings'
  | 'events'
  | 'groups'
  | 'settings'
  | 'shop'
  | 'tools'
  | 'users';

export interface PermissionRequirement {
  keys: AdminPermissionKey[];
  action: PermissionAction;
}

export const permissionActionForMethod = (method?: string): PermissionAction =>
  ['GET', 'HEAD', 'OPTIONS'].includes((method || '').toUpperCase())
    ? 'read'
    : 'write';

const pathnameFromUrl = (url = '') => {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url.split('?')[0];
  }
};

export const permissionRequirementForApiRequest = (
  url?: string,
  method?: string
): PermissionRequirement | null => {
  const path = pathnameFromUrl(url);
  const action = permissionActionForMethod(method);
  const isRead = action === 'read';

  if (path === '/access/me' || path.startsWith('/stats')) return null;
  if (path.startsWith('/cigar-recognition')) {
    return { keys: ['cigars'], action };
  }
  if (
    path.startsWith('/roles') ||
    path.startsWith('/permissions') ||
    path.startsWith('/team')
  ) {
    return { keys: ['settings'], action };
  }
  if (path.startsWith('/cigars/pairings')) {
    return { keys: ['drink_pairings'], action };
  }
  if (path.startsWith('/cigars')) {
    return {
      keys: isRead ? ['cigars', 'shop', 'drink_pairings'] : ['cigars'],
      action,
    };
  }
  if (path.startsWith('/brands')) {
    return {
      keys: isRead
        ? ['cigars', 'businesses', 'events']
        : ['cigars', 'businesses'],
      action,
    };
  }
  if (
    path.startsWith('/business-claims') ||
    path.startsWith('/businesses/datatables/claims') ||
    path.startsWith('/venues/datatables/claims')
  ) {
    return { keys: ['BusinessClaimsApproval'], action };
  }
  if (path.startsWith('/businesses')) {
    return { keys: ['businesses'], action };
  }
  if (path.startsWith('/venues')) {
    return {
      keys: isRead ? ['businesses', 'events', 'shop'] : ['businesses'],
      action,
    };
  }
  if (path.startsWith('/groups')) return { keys: ['groups'], action };
  if (path.startsWith('/events')) return { keys: ['events'], action };
  if (path.startsWith('/shop')) return { keys: ['shop'], action };
  if (path.startsWith('/users')) {
    return {
      keys: isRead ? ['users', 'events', 'shop'] : ['users'],
      action,
    };
  }
  if (path.startsWith('/feed')) return { keys: ['blog'], action };
  if (path.startsWith('/sitemaps')) return { keys: ['tools'], action };
  if (path.startsWith('/scrape/cigar')) return { keys: ['cigars'], action };
  if (path.startsWith('/scrape/product')) return { keys: ['shop'], action };
  if (path.startsWith('/scrape/event')) return { keys: ['events'], action };
  if (path.startsWith('/scrape')) return { keys: ['tools'], action };
  if (path.startsWith('/actions/cigars')) return { keys: ['cigars'], action };
  if (path.startsWith('/actions/products')) return { keys: ['shop'], action };
  if (path.startsWith('/actions/ads')) return { keys: ['ad_network'], action };
  if (path.startsWith('/actions/feed')) return { keys: ['blog'], action };
  if (path.startsWith('/actions')) return { keys: ['tools'], action };
  return null;
};

export const defaultAdminPathForPermissions = (
  canRead: (key: string) => boolean
) => {
  const destinations: Array<[AdminPermissionKey, string]> = [
    ['cigars', '/cigars/manager'],
    ['BusinessClaimsApproval', '/businesses/claims'],
    ['businesses', '/venues/manager'],
    ['shop', '/shop/manager'],
    ['events', '/events/manager'],
    ['groups', '/groups/manager'],
    ['drink_pairings', '/drink-pairings/manager'],
    ['users', '/users/manager'],
    ['tools', '/actions'],
    ['settings', '/settings'],
  ];
  return destinations.find(([key]) => canRead(key))?.[1] || null;
};
